// lib/claveDeTema.js — TANDA 29 (#270). LA CLAVE DE TEMA, DEL LADO DEL CLIENTE.
//
// Gemelo EXACTO de `backend/routes/study.py::clave_de_tema`:
//
//     clave_de_tema(fila) = (fila.topic or "").strip(RECORTE_DE_CLAVE) or "General"
//
// Vale para preguntas y para flashcards, y es lo único que define la identidad
// de un tema — porque un tema NO TIENE ID. El censo sobre 400 documentos
// encontró que las únicas llaves de `documents.topics` son `name` y
// `description` (censo de temas §H.2): la identidad de un tema ES su string.
//
// POR QUÉ ESTE ARCHIVO EXISTE, Y POR QUÉ ESTÁ VACÍO DE TODO LO DEMÁS. Lo
// consumen el armado del pool local de los TRES modos (brain/examRuntime,
// brain/sessionRuntime, brain/flashcardsRuntime) y el ensamblador del selector
// sin señal (warehouse/screenSources). Cuatro sitios, una sola regla escrita una
// sola vez: si la clave se escribiera dos veces, el día que una cambiara el
// usuario palomearía una casilla y recibiría otra cosa.
//
// NO IMPORTA NADA. Es hoja a propósito: un `import api` aquí arriba tumbaría el
// chasis de 28 suites (regla aprendida en tandas anteriores), y este módulo lo
// carga el runtime local, que es justo el camino que no puede depender de red.

// HOTFIX RACIONALES (#338) — EL RECORTE DEJA DE DELEGARSE AL LENGUAJE.
//
// Hasta hoy este gemelo recortaba con `.trim()` y el de Python con `.strip()`, y
// el comentario de los dos decia "byte a byte la misma regla". No lo era: los
// dos runtimes recortan conjuntos DISTINTOS. Medido ejecutandolos sobre los
// primeros 69,632 code points:
//
//     solo Python .strip():  U+001C U+001D U+001E U+001F U+0085
//     solo JS .trim():       U+FEFF
//
// U+FEFF es la marca de orden de bytes, que es justo lo que un PDF mal exportado
// deja al principio de una linea. Con ella, el servidor agrupaba un tema y el
// telefono agrupaba OTRO: la casilla que el usuario palomea se construye con una
// de las dos y el pool se filtra con la otra, asi que palomear un tema podia no
// devolver nada.
//
// La lista de abajo es la UNION de las dos, explicita y en el mismo orden que
// `RECORTE_DE_CLAVE_PUNTOS` de `backend/routes/study.py`. EXPLICITA porque un
// conjunto que depende de la version del runtime no es una regla, es una
// coincidencia. Y POR CODE POINT, no por caracter literal: la mitad de estos 30
// son invisibles, y un invisible en el fuente es justo lo que nadie ve al
// revisar un diff. `claveDeTema.paridad.test.js` fija que las dos listas
// declaren los mismos numeros: tocar una sin la otra truena.
//
// Medido antes de cambiarlo: de los 4,482 temas distintos de `questions`, los
// 4,698 de `flashcards` y los 4,508 nombres de `documents.topics`, CERO empiezan
// o terminan con alguno de estos 30. Sobre los datos de hoy el cambio es inerte;
// lo que cierra es el hueco de mañana.
export const RECORTE_DE_CLAVE_PUNTOS = [
  0x0009, 0x000A, 0x000B, 0x000C, 0x000D,
  0x001C, 0x001D, 0x001E, 0x001F,
  0x0020, 0x0085, 0x00A0, 0x1680,
  0x2000, 0x2001, 0x2002, 0x2003, 0x2004, 0x2005,
  0x2006, 0x2007, 0x2008, 0x2009, 0x200A,
  0x2028, 0x2029, 0x202F, 0x205F, 0x3000, 0xFEFF,
];

export const RECORTE_DE_CLAVE = RECORTE_DE_CLAVE_PUNTOS
  .map((cp) => String.fromCodePoint(cp)).join('');

/**
 * El recorte por los DOS extremos de cualquier caracter de `RECORTE_DE_CLAVE`.
 * Gemelo exacto de `str.strip(chars)` de Python, que es lo que usa el servidor.
 * Los 30 son del plano basico, asi que indexar por unidad de codigo es seguro.
 */
function recortar(texto) {
  let ini = 0;
  let fin = texto.length;
  while (ini < fin && RECORTE_DE_CLAVE.includes(texto[ini])) ini += 1;
  while (fin > ini && RECORTE_DE_CLAVE.includes(texto[fin - 1])) fin -= 1;
  return texto.slice(ini, fin);
}

/** La clave de tema de una fila de pregunta o de flashcard. */
export function claveDeTema(fila) {
  return recortar(((fila && fila.topic) || '')) || 'General';
}

/**
 * El recorte de un pool por temas palomeados.
 *
 * EL CANDADO: sin selección (`null`, `undefined` o `[]`) devuelve el MISMO
 * arreglo, sin copiarlo y sin recorrerlo — el camino de hoy no paga nada por
 * esta función. Sólo una lista NO VACÍA recorta.
 *
 * La comparación es TEXTO EXACTO contra la clave: sin fuzzy, sin `toLowerCase`,
 * sin quitar acentos. No hace falta ninguna de las tres — la casilla que el
 * usuario palomeó se construyó con esta MISMA función sobre estas MISMAS filas,
 * así que coincide sola. Meter parecido aquí sólo podría FUNDIR dos temas que
 * Call A emitió distintos.
 *
 * Las filas entran ENTERAS: esto no recorta campos, no reescribe nada y no
 * regenera nada. La pregunta que pasa el filtro es el mismo objeto que habría
 * entrado sin él.
 */
export function filtrarPorTemas(pool, temas) {
  if (!temas || temas.length === 0) return pool;
  const queridos = new Set(temas);
  return pool.filter((fila) => queridos.has(claveDeTema(fila)));
}

export default claveDeTema;
